import { createContext, useContext } from "react";

/**
 * ConfigOutline — bridges the ProjectConfigPage section list into the AppShell
 * sidebar. The config page (mockup project-config.html L344-L381) shows its
 * 6 sections as a nested sub-nav under the active "Config" item, each with a
 * scroll-spy active state and a per-section "modified" dot.
 *
 * ProjectConfigPage publishes its outline on mount (and on every dirty /
 * active-section change) and clears it on unmount. The AppShell sidebar reads
 * it. When the config page isn't mounted the outline is empty and no sub-nav
 * renders.
 *
 * The context and its hook live here rather than beside the provider, so
 * ConfigOutline.tsx exports a component and nothing else and stays
 * hot-replaceable.
 */

export interface ConfigOutlineSection {
  /** DOM id of the section card — also the scroll-spy anchor target. */
  id: string;
  label: string;
  dirty: boolean;
}

export interface ConfigOutlineState {
  sections: ConfigOutlineSection[];
  activeId: string;
}

export interface ConfigOutlineValue {
  outline: ConfigOutlineState;
  setOutline: (next: ConfigOutlineState) => void;
}

export const EMPTY_OUTLINE: ConfigOutlineState = { sections: [], activeId: "" };

/** No-op fallback so the config page works in tests without a provider. */
const NOOP: ConfigOutlineValue = {
  outline: EMPTY_OUTLINE,
  setOutline: () => {},
};

export const ConfigOutlineContext = createContext<ConfigOutlineValue | null>(
  null,
);

/** Returns the outline context, or a no-op fallback when no provider exists. */
export function useConfigOutline(): ConfigOutlineValue {
  return useContext(ConfigOutlineContext) ?? NOOP;
}
